import React, { useState, useEffect, useCallback } from "react";
import './Admin.css';
import userAPI from "../../context/Api";
import Header from '../../components/Header/Header';

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [alert, setAlert] = useState(null);
  const [editUserId, setUpdateUserId] = useState(null);
  const [editUserData, setEditUserData] = useState({ username: '', email: '', password: '', status: '' });

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showAlert('Unauthorized: No token found', 'error');
      return;
    }

    setFetchingUsers(true);
    try {
      const response = await userAPI.getAllUsers(token);
      if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else {
        console.error("Unexpected data format:", response.data);
        showAlert('Failed to fetch users: Invalid data format', 'error');
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      showAlert('Failed to fetch users', 'error');
    } finally {
      setFetchingUsers(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return showAlert('Unauthorized: No token found', 'error');

    setCreatingUser(true);
    try {
      const response = await userAPI.createUser(newUser, token);
      setUsers((prevUsers) => [...prevUsers, response.data]);
      setNewUser({ username: '', email: '', password: '' });
      showAlert(`User ${newUser.username} created successfully!`);
    } catch (error) {
      showAlert(error.response?.data?.message || 'User creation failed', 'error');
    } finally {
      setCreatingUser(false);
    }
  };

  const confirmUpdate = async (userId, updatedUserData) => {
    if (window.confirm('Are you sure you want to edit this user?')) {
      const token = localStorage.getItem('token');
      if (!token) return showAlert('Unauthorized: No token found', 'error');

      setFetchingUsers(true);
      try {
        if (!updatedUserData.password) delete updatedUserData.password;
        const updatedUser = await userAPI.updateUser(userId, updatedUserData, token);
        setUsers(users.map(user => (user.id === userId ? { ...user, ...updatedUser } : user)));
        setUpdateUserId(null);
        showAlert('User updated successfully');
      } catch (error) {
        console.log(error);
        showAlert('Failed to update user', 'error');
      } finally {
        setFetchingUsers(false);
      }
    }
  };

  const confirmDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      const token = localStorage.getItem('token');
      if (!token) return showAlert('Unauthorized: No token found', 'error');

      setFetchingUsers(true);
      try {
        await userAPI.deleteUser(userId, token);
        setUsers(users.filter(user => user.id !== userId));
        showAlert('User deleted successfully');
      } catch (error) {
         
        showAlert('Unauthorized: Thica accounts cannot be deleted', 'error');
      } finally {
        setFetchingUsers(false);
      }
    }
  };

  const formatDateToIST = (isoDate) => {
    if (!isoDate) return 'N/A';
  
    const date = new Date(isoDate);
  
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',      // Convert to IST
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',        // Show GMT+5:30
    });
  };
  

  const filteredUsers = users
    .filter((user) => typeof user.username === 'string' && user.username.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => (a.status === 'active' && b.status !== 'active' ? -1 : 1));

    return (
      <div>
        <Header />
        <div className="dashboard-container">
          {alert && <div className={`alert ${alert.type}`}>{alert.message}</div>}
  
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
  
          <div className="create-user-section">
            <h2>Create New User</h2>
            <form onSubmit={handleCreateUser} className="user-form">
              <input type="text" placeholder="Username" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} required />
              <input type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
              <input type="password" placeholder="Password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
              <button type="submit" className="gradient-btn" disabled={creatingUser}>{creatingUser ? 'Creating...' : 'Create User'}</button>
            </form>
          </div>
  
          <div className="users-list">
            <h2>Manage Users ({filteredUsers.length})</h2>
            {fetchingUsers ? (
              <div className="loading">Loading users...</div>
            ) : (
              <div className="user-grid">
                {filteredUsers.map(user => (
                  <div key={user.id} className="user-card">
                    <div>
                      <h3>{user.username}</h3>
                      <p className="user-info-item">
                        <span className="info-key">Status:</span>
                        <span className={`status-indicator ${user.status === 'active' ? 'active' : 'inactive'}`}
                          style={{ color: user.status === 'active' ? 'green' : 'red' }}>
                          {user.status || 'Unknown'}
                          <span className="status-icon">{user.status === 'active' ? '🟢' : '🔴'}</span>
                        </span>
                      </p>
                      <p className="user-info-item">
                        <span className="info-key">Last Login:</span>
                        <span className="info-value">{formatDateToIST(user.last_login) || 'N/A'}</span>
                      </p>
                      <p className="user-info-item">
                        <span className="info-key">IP Address:</span>
                        <span className="info-value">{user.ip_address || 'N/A'}</span>
                      </p>
                    </div>
                    
                    {editUserId === user.id ? (
                      <form className="edit-user-form" onSubmit={(e) => { e.preventDefault(); confirmUpdate(user.id, editUserData); }}>
                        <input type="text" value={editUserData.username} onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })} required />
                        <input type="email" value={editUserData.email} onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })} required />
                        <input type="password" value={editUserData.password} onChange={(e) => setEditUserData({ ...editUserData, password: e.target.value })} placeholder="New Password" />
                       
                        <button type="submit" className="save-btn">Save</button>
                        <button type="button" className="cancel-btn" onClick={() => setUpdateUserId(null)}>Cancel</button>
                      </form>
                    ) : (
                      <>
                       
                        <div className="user-actions">
                          <button className="edit-btn" onClick={() => { setUpdateUserId(user.id); setEditUserData(user); }}>Edit</button>
                          <button className="delete-btn" onClick={() => confirmDelete(user.id)}>Delete</button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  export default AdminPanel;
  